/**
 * Personal-step fields (Task 7: split name, verified email, two-field role,
 * LinkedIn) — docs/superpowers plan referenced in
 * .planning/ONBOARDING-REDESIGN-DECISIONS.md decision 5.
 *
 * `founderStatus` (Founder / Co-founder / Team member — see
 * `./founder-status`) and `functionTitle` (CEO/CTO/.../Other) are genuinely
 * orthogonal: a CTO can be a co-founder or a hired employee. Neither is
 * derived from the other any more — both are asked explicitly.
 */
import type { FounderStatus } from './founder-status';

export type PersonalFields = {
  firstName: string;
  lastName: string;
  founderStatus: FounderStatus;
  functionTitle: string;
};

export const FUNCTION_OPTIONS: string[] = ['CEO', 'CTO', 'COO', 'CFO', 'Head of…', 'Other'];

/** The picklist sentinel that reveals a free-text field for a custom function. */
export const FUNCTION_OTHER = 'Other';

/**
 * Returns error keys (empty = valid). `requireFounderStatus` is false on the
 * invite path where the value is preset (read-only) from the accepted
 * invite's membership row rather than asked — a missing value there reflects
 * an upstream data gap, not a user input error.
 *
 * `functionTitle` is always required (Task 3): it's asked on every personal
 * step (founder and invitee alike). The screen resolves the "Other → text"
 * picklist to a concrete string before validating, so an empty value here
 * means the user neither picked a preset nor typed a custom one.
 */
export function validatePersonalFields(
  f: PersonalFields,
  opts: { requireFounderStatus: boolean }
): string[] {
  const errors: string[] = [];
  if (!f.firstName?.trim()) errors.push('firstName');
  if (!f.lastName?.trim()) errors.push('lastName');
  if (opts.requireFounderStatus && !f.founderStatus) errors.push('founderStatus');
  if (!f.functionTitle?.trim()) errors.push('functionTitle');
  return errors;
}
